import {
  LOYALTY_REDEMPTION_BLOCKS,
  type LoyaltyCustomerStatus,
  type LoyaltyProgram,
  type LoyaltyRedemptionBlock,
  quoteRedemption,
  type RedemptionQuote,
} from '@sahab/contracts'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { api, isApiError } from '../../lib/api'
import { useCan, useModuleEnabled } from '../auth/session'
import type { CartRedemption } from './cart'

/**
 * The till's own view of loyalty points (ADR-044): the programme, read once per till session, and the
 * named customer's points. Kept under ['pos', 'loyalty'] so a sale made at the till refreshes them.
 */
export const tillLoyaltyKeys = {
  all: ['pos', 'loyalty'] as const,
  program: ['pos', 'loyalty', 'program'] as const,
  customers: ['pos', 'loyalty', 'customers'] as const,
  customer: (id: string) => ['pos', 'loyalty', 'customers', id] as const,
}

export const tillLoyaltyProgramQueryOptions = queryOptions({
  queryKey: tillLoyaltyKeys.program,
  queryFn: ({ signal }) => api.get<LoyaltyProgram>('/loyalty/program', { signal }),
  // Once per till session: a refusal of the sale (409 LOYALTY_REDEMPTION_CHANGED) reads it again.
  staleTime: Number.POSITIVE_INFINITY,
})

export function tillLoyaltyCustomerQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: tillLoyaltyKeys.customer(customerId),
    queryFn: ({ signal }) =>
      api.get<LoyaltyCustomerStatus>(`/loyalty/customers/${encodeURIComponent(customerId)}`, { signal }),
  })
}

export interface TillLoyalty {
  /** The company has the module and the cashier may see points: the till shows them. */
  on: boolean
  /** The cashier may take points off a sale (loyalty.redeem). */
  canRedeem: boolean
  program: LoyaltyProgram | undefined
  /** The named customer's points (undefined for the walk-in customer, while loading or on an error). */
  status: LoyaltyCustomerStatus | undefined
}

/**
 * Loyalty at the till: nothing is read unless the company has the module and the cashier holds
 * loyalty.redeem or loyalty.read; the customer's points only for a named customer (`customerId`).
 */
export function useTillLoyalty(customerId: string | null): TillLoyalty {
  const moduleOn = useModuleEnabled('loyalty')
  const canRedeem = useCan('loyalty.redeem')
  const canRead = useCan('loyalty.read')
  const on = moduleOn && (canRedeem || canRead)
  const program = useQuery({ ...tillLoyaltyProgramQueryOptions, enabled: on })
  const status = useQuery({
    ...tillLoyaltyCustomerQueryOptions(customerId ?? ''),
    enabled: on && customerId !== null,
  })
  return {
    on,
    canRedeem: on && canRedeem,
    program: on ? program.data : undefined,
    status: on && customerId !== null ? status.data : undefined,
  }
}

/** What a sale with `goods` (and a sale discount or not) can redeem for this customer now. */
export function quoteForSale(
  program: LoyaltyProgram,
  status: LoyaltyCustomerStatus,
  goods: string,
  hasSaleDiscount: boolean,
  requested: number,
): RedemptionQuote {
  return quoteRedemption({
    program,
    member: status.member,
    available: status.member?.balance.available ?? 0,
    goods,
    hasSaleDiscount,
    requested,
  })
}

export const LOYALTY_REFUSALS = ['LOYALTY_REDEMPTION_CHANGED', 'LOYALTY_BALANCE_CHANGED'] as const
export type LoyaltyRefusalCode = (typeof LOYALTY_REFUSALS)[number]

/** The API refused the points on a sale: what it says the sale or the balance allows now. */
export interface LoyaltyRefusal {
  code: LoyaltyRefusalCode
  /** LOYALTY_REDEMPTION_CHANGED: the most the sale can take now (0 when blocked). */
  maxPoints: number | null
  /** The member's spendable points now. */
  availablePoints: number | null
  /** What a point is worth now. */
  pointValue: string | null
  blocks: LoyaltyRedemptionBlock[]
}

const whole = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null

/**
 * A 409 refusing the points on a sale (`maxPoints`, `availablePoints`, `pointValue`, `blocks` in the
 * problem document), or null for any other error.
 */
export function loyaltyRefusal(error: unknown): LoyaltyRefusal | null {
  if (!isApiError(error)) return null
  const code = LOYALTY_REFUSALS.find((c) => c === error.code)
  if (!code) return null
  const { maxPoints, availablePoints, pointValue, blocks } = error.extensions
  return {
    code,
    maxPoints: whole(maxPoints),
    availablePoints: whole(availablePoints),
    pointValue: typeof pointValue === 'string' ? pointValue : null,
    blocks: Array.isArray(blocks)
      ? blocks.filter((b): b is LoyaltyRedemptionBlock =>
          (LOYALTY_REDEMPTION_BLOCKS as readonly unknown[]).includes(b),
        )
      : [],
  }
}

/**
 * The points a refused sale can go on with: what was applied, capped by what the refusal says the
 * sale (maxPoints) or the balance (availablePoints) allows now. Never more than was applied.
 */
export function pointsAfterRefusal(applied: CartRedemption, refusal: LoyaltyRefusal): CartRedemption | null {
  const caps = [applied.points, refusal.maxPoints, refusal.availablePoints].filter(
    (n): n is number => n !== null,
  )
  const points = Math.min(...caps)
  if (points <= 0) return null
  return { points, pointValue: refusal.pointValue ?? applied.pointValue }
}
